import axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { mdiAlertCircle, mdiCheckCircle, mdiBellOff, mdiLoading } from '@mdi/js';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import Icon from '../../components/Icon';
import Badge from '../../components/layout/Badge';
import { Alerta, ALERTAS_QUERY_KEY, useAlertas } from '@/lib/useAlertas';

const dataHora = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

const CartaoAlerta = ({ alerta }: { alerta: Alerta }) => {
    const queryClient = useQueryClient();
    const ativo = alerta.estado === 'firing';

    const ackMutation = useMutation(
        async () => axios.post(`/api/alerta/${alerta.id}/ack`, {}, { withCredentials: true }),
        {
            onSuccess: () => {
                toast.success('Lembretes suspensos. Você ainda será avisado quando o problema for resolvido.');
                queryClient.invalidateQueries(ALERTAS_QUERY_KEY);
            },
            onError: () => toast.error('Erro ao suspender os lembretes'),
        },
    );

    return (
        // div em vez de HBox/VBox: os dois forçam width 100% e, aninhados,
        // espremem a coluna do texto.
        <div
            style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `1px solid ${ativo ? 'var(--tc-color-danger)' : 'var(--tc-color-gray-200)'}`,
            }}
        >
            {ativo ? (
                <span className="notif notif-danger" style={{ marginTop: 2, flexShrink: 0 }}>
                    <Icon path={mdiAlertCircle} size={0.9} color="var(--tc-color-danger)" />
                </span>
            ) : (
                <span style={{ marginTop: 2, flexShrink: 0, lineHeight: 0 }}>
                    <Icon path={mdiCheckCircle} size={0.9} color="var(--tc-color-success)" />
                </span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <b>{alerta.titulo}</b>
                    {ativo ? <Badge variant="danger">Ativo</Badge> : <Badge variant="success">Resolvido</Badge>}
                    {ativo && alerta.data_ack ? <Badge variant="outline-secondary">Lembretes suspensos</Badge> : null}
                </div>
                {alerta.mensagem ? (
                    <span style={{ fontSize: '0.9em', color: 'var(--tc-color-gray-700)', overflowWrap: 'anywhere' }}>
                        {alerta.mensagem}
                    </span>
                ) : null}
                {ativo && alerta.orientacao ? (
                    <div
                        style={{
                            marginTop: 4,
                            padding: '8px 12px',
                            borderRadius: 6,
                            background: 'var(--tc-color-gray-50)',
                            fontSize: '0.9em',
                        }}
                    >
                        <b>Como resolver: </b>
                        {alerta.orientacao}
                    </div>
                ) : null}
                <small style={{ color: 'var(--tc-color-gray-600)' }}>
                    Desde {dataHora(alerta.data_inicio)}
                    {alerta.data_resolucao ? ` · resolvido em ${dataHora(alerta.data_resolucao)}` : ''}
                    {alerta.data_ack ? ` · lembretes suspensos em ${dataHora(alerta.data_ack)}` : ''}
                </small>
            </div>
            {ativo ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                    {alerta.link ? (
                        <Link to={alerta.link}>
                            <Botao variant="outline-primary" size="small">Ver</Botao>
                        </Link>
                    ) : null}
                    {!alerta.data_ack ? (
                        <Botao
                            variant="outline-secondary"
                            size="small"
                            icon={ackMutation.isLoading ? mdiLoading : mdiBellOff}
                            disabled={ackMutation.isLoading}
                            title="Para de enviar lembretes por e-mail sobre este problema. Você ainda será avisado quando ele for resolvido."
                            onClick={() => ackMutation.mutate()}
                        >
                            Suspender lembretes
                        </Botao>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

const SecaoAlertas = () => {
    const { alertas, ativos, isLoading, isError } = useAlertas();

    return (
        <VBox gap="16px">
            <VBox gap="4px">
                <h3 style={{ margin: 0 }}>Alertas</h3>
                <small style={{ color: 'var(--tc-color-gray-600)' }}>
                    Problemas detectados pelo sistema, como falhas de envio em canais. Os e-mails de alerta vão para
                    os endereços administrativos inscritos em Notificações do Sistema, com lembrete a cada 4 horas
                    enquanto o problema durar.
                </small>
            </VBox>

            {isLoading ? (
                <HBox gap="8px" style={{ alignItems: 'center' }}>
                    <Icon path={mdiLoading} size={0.8} spin /> Carregando...
                </HBox>
            ) : isError ? (
                <span style={{ color: 'var(--tc-color-danger)' }}>Erro ao carregar os alertas.</span>
            ) : alertas.length === 0 ? (
                <HBox gap="8px" style={{ alignItems: 'center', color: 'var(--tc-color-gray-700)' }}>
                    <Icon path={mdiCheckCircle} size={0.8} color="var(--tc-color-success)" />
                    Nenhum alerta nos últimos 30 dias.
                </HBox>
            ) : (
                <>
                    {ativos.length === 0 ? (
                        <HBox gap="8px" style={{ alignItems: 'center', color: 'var(--tc-color-gray-700)' }}>
                            <Icon path={mdiCheckCircle} size={0.8} color="var(--tc-color-success)" />
                            Nenhum alerta ativo. Abaixo, os resolvidos nos últimos 30 dias.
                        </HBox>
                    ) : null}
                    {alertas.map((alerta) => (
                        <CartaoAlerta key={alerta.id} alerta={alerta} />
                    ))}
                </>
            )}
        </VBox>
    );
};

export default SecaoAlertas;
